/**
 * Number formatting utility for locale-aware number display.
 *
 * Romanian uses comma as decimal separator (3,14) and period as thousands
 * separator (1.000) in non-math UI text. Mathematical expressions keep
 * international format (3.14).
 */

import i18n from '@/i18n/config';

/**
 * Formats a number for UI display using the current locale conventions.
 * Romanian: 1.000 (thousands separator is period, decimal is comma)
 * English: 1,000 (thousands separator is comma, decimal is period)
 *
 * @param value - The number to format
 * @param options - Intl.NumberFormatOptions for customization
 * @returns Locale-formatted number string
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  const locale = i18n.language === 'ro' ? 'ro-RO' : 'en-US';
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Formats a number as a percentage for UI display.
 * Does NOT include the % sign (caller can add it).
 *
 * @param value - The percentage value (e.g., 85.5 for 85.5%)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Locale-formatted number string
 */
export function formatPercent(value: number, decimals = 0): string {
  const locale = i18n.language === 'ro' ? 'ro-RO' : 'en-US';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
